import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import useDecodeToken from "../../../../hooks/useDecodeToken";

import { getOrderHistorySearch } from "../../../../services/deliveryService";

import { orderSearchCategories } from "../../../../constants";
import {
  checkIfClient,
  API_CONSTANTS,
  retryAttempt,
  queryKeys,
  RENDER_MODE,
} from "../constants";

const useSSRStatuses = ({
  searchCategory,
  fromDate,
  toDate,
  columnsFilter = [],
  columnsSorting = [],
  pageIndex = 0,
  pageSize = 10,
  invoices = [],
  barcodes = [],
  isServerSide = true,
}) => {
  const [decodedToken] = useDecodeToken();
  const history = useHistory();
  const queryClient = useQueryClient();

  const isClient = useMemo(() => checkIfClient(decodedToken), [decodedToken]);

  const searchStatusParams = useMemo(() => {
    const baseParams = {
      searchBy: searchCategory,
      isClient,
      pageIndex,
      pageSize,
      columnsFilter,
      columnsSorting,
    };

    if (searchCategory === orderSearchCategories.dateRange.value) {
      return {
        ...baseParams,
        fromDate,
        toDate,
      };
    }

    if (searchCategory === orderSearchCategories.invoice.value) {
      return {
        ...baseParams,
        selected: invoices,
      };
    }

    if (searchCategory === orderSearchCategories.barcode.value) {
      return {
        ...baseParams,
        selected: barcodes,
      };
    }
  }, [
    searchCategory,
    isClient,
    fromDate,
    toDate,
    pageIndex,
    pageSize,
    columnsFilter,
    columnsSorting,
    invoices,
    barcodes,
  ]);

  const orderStatusQuery = useQuery({
    queryKey: queryKeys.STATUS_ORDERS_BY(searchCategory, searchStatusParams),
    queryFn: () =>
      getOrderHistorySearch({
        ...searchStatusParams,
        timeout: API_CONSTANTS.TIMEOUT,
        tableMode: RENDER_MODE.SSR,
        skipCXTStatus: false,
      }),
    retry: retryAttempt,
    select: (response) => response?.data,

    meta: { history },
    staleTime: API_CONSTANTS.STALE_TIME,
    cacheTime: API_CONSTANTS.GC_TIME,
    keepPreviousData: true,
    enabled: isServerSide,
  });

  // useEffect(() => {
  //   const MAX_PAGES = Math.ceil(
  //     (orderStatusQuery?.data?.total || 0) / pageSize,
  //   );
  //   const currentPage = pageIndex;

  //   // prefetch next page if it exists
  //   if (currentPage < MAX_PAGES - 1 && MAX_PAGES > 1) {
  //     const nextPageIndex = currentPage + 1;

  //     queryClient.prefetchQuery({
  //       queryKey: queryKeys.STATUS_ORDERS_BY(searchCategory, {
  //         ...searchStatusParams,
  //         pageIndex: nextPageIndex,
  //       }),
  //       queryFn: () =>
  //         getOrderHistorySearch({
  //           ...searchStatusParams,
  //           pageIndex: nextPageIndex,
  //           timeout: API_CONSTANTS.TIMEOUT,
  //           tableMode: RENDER_MODE.SSR,
  //           skipCXTStatus: false,
  //         }),
  //       retry: retryAttempt,
  //       // select: (response) => response?.data,
  //       staleTime: API_CONSTANTS.STALE_TIME,
  //       cacheTime: API_CONSTANTS.GC_TIME,
  //       keepPreviousData: true,
  //       enabled: isServerSide,
  //     });
  //   }
  // }, [
  //   isServerSide,
  //   orderStatusQuery?.data?.total,
  //   pageIndex,
  //   pageSize,
  //   queryClient,
  //   searchCategory,
  //   searchStatusParams,
  // ]);

  useEffect(() => {
    let mount = true;
    const statuses = orderStatusQuery?.data?.statuses;

    if (
      !mount ||
      !isServerSide ||
      orderStatusQuery?.isFetching ||
      !Array.isArray(statuses)
    )
      return;

    statuses.forEach(({ deliveryId, status }) => {
      queryClient.setQueryData(["orderStatus", deliveryId], (old) =>
        old?.status === status ? old : { deliveryId, status },
      );
    });

    return () => {
      mount = false;
    };
  }, [
    isServerSide,
    orderStatusQuery.data,
    queryClient,
    orderStatusQuery?.isFetching,
  ]);

  return orderStatusQuery;
};

export default useSSRStatuses;
